// src/pages/Advertisement/CountryChoice.tsx
import React from "react";

export type CountriesDropdownProps = {
  label: string;
  options: CountryOption[]; 
  value: string[];
  onToggle: (country: string) => void;
  placeholder?: string;
  labelStyle: React.CSSProperties;
  selectStyle: React.CSSProperties;
};


export const ADS_BLOCKED: string[] = [
  "AF", // Afghanistan
  "CU", // Cuba
  "IR", // Iran
  "KP", // North Korea
  "SY", // Syria
  "RU",
  "BY",
  "VE",
  "MM",
  "SD",
  "SS",
  "YE",
  "PS",
  "CN",
  "ER",
  "LY",

];

const CountriesDropdown: React.FC<CountriesDropdownProps> = ({
  label,
  options,
  value,
  onToggle,
  placeholder = "Select countries",
  labelStyle,
  selectStyle,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // close on outside click
  React.useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!open) return;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // close on Escape
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // focus search when opening + clear query when closing
  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
    }
  }, [open]);

  const summary =
    value.length === 0
      ? placeholder
      : value.length <= 2
        ? value.join(", ")
        : `${value.length} selected`;

  const normalizedQuery = query.trim().toLowerCase();

  // filter immediately after the 1st letter typed
  const filteredOptions = React.useMemo(() => {
    // 1. remove blocked countries
    const allowed = options.filter(
      (c) => !ADS_BLOCKED.includes(c.code)
    );
  
    // 2. apply search
    const searched =
      normalizedQuery.length === 0
        ? allowed
        : allowed.filter((c) =>
            c.name.toLowerCase().startsWith(normalizedQuery)
          );
  
    // 3. sort alphabetically
    return [...searched].sort((a, b) => a.name.localeCompare(b.name));
  }, [options, normalizedQuery]);
  
  
  return (
    <div style={labelStyle}>
      {label}

      {/* looks like your continent <select> */}
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...selectStyle,
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </span>
        <span style={{ opacity: 0.8 }}>▾</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="listbox"
          aria-multiselectable="true"
          style={{
            marginTop: 0,
            backgroundColor: "#020617",
            borderRadius: 16,
            border: "1px solid rgba(148,163,184,0.8)",
            boxShadow: "0 18px 40px rgba(0,0,0,0.6)",
            padding: 6,
            width: "95%",
          }}
        >
          {/* search box */}
          <div style={{ padding: 6 }}>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search countries…"
              style={{
                width: "90%",
                ...selectStyle,
                borderRadius: 12,
                padding: "10px 12px",
                backgroundColor: "rgba(2,6,23,0.65)",
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
              }}
            />
          </div>

    {/* scrollable list */}
<div
  style={{
    maxHeight: 240,
    overflowY: "auto",
    padding: 6,
  }}
>
  {filteredOptions.length === 0 ? (
    <div style={{ padding: "10px 10px", color: "rgba(226,232,240,0.8)" }}>
      No matches
    </div>
  ) : (
    filteredOptions.map((country) => {
      const checked = value.includes(country.code); // ✅ value should store ISO codes
      return (
        <button
          key={country.code} // ✅ stable key
          type="button"
          onClick={() => onToggle(country.code)} // ✅ toggle by ISO code
          style={{
            width: "100%",
            textAlign: "left",
            padding: "8px 10px",
            borderRadius: 12,
            border: "none",
            background: checked ? "rgba(250, 204, 21, 0.18)" : "transparent",
            color: "#e5e7eb",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              border: "1px solid rgba(148,163,184,0.85)",
              backgroundColor: checked ? "rgba(250, 204, 21, 0.95)" : "transparent",
              display: "inline-block",
            }}
          />
          <span>{country.name}</span> {/* ✅ show name */}
        </button>
      );
    })
  )}
</div>

        </div>
      )}
    </div>
  );
};

export default CountriesDropdown;


// Country type with ISO 3166-1 alpha-2 codes
export type CountryOption = { code: string; name: string };

// --- larger continent + countries model (ISO codes) ---
export const CONTINENT_COUNTRIES: Record<string, CountryOption[]> = {
  Europe: [
    { code: "AL", name: "Albania" },
    { code: "AD", name: "Andorra" },
    { code: "AT", name: "Austria" },
    { code: "BY", name: "Belarus" },
    { code: "BE", name: "Belgium" },
    { code: "BA", name: "Bosnia and Herzegovina" },
    { code: "BG", name: "Bulgaria" },
    { code: "HR", name: "Croatia" },
    { code: "CY", name: "Cyprus" },
    { code: "CZ", name: "Czechia" },
    { code: "DK", name: "Denmark" },
    { code: "EE", name: "Estonia" },
    { code: "FI", name: "Finland" },
    { code: "FR", name: "France" },
    { code: "DE", name: "Germany" },
    { code: "GR", name: "Greece" },
    { code: "HU", name: "Hungary" },
    { code: "IS", name: "Iceland" },
    { code: "IE", name: "Ireland" },
    { code: "IT", name: "Italy" },
    { code: "XK", name: "Kosovo" }, // not officially ISO-assigned in ISO 3166-1; commonly used code
    { code: "LV", name: "Latvia" },
    { code: "LI", name: "Liechtenstein" },
    { code: "LT", name: "Lithuania" },
    { code: "LU", name: "Luxembourg" },
    { code: "MT", name: "Malta" },
    { code: "MD", name: "Moldova" },
    { code: "MC", name: "Monaco" },
    { code: "ME", name: "Montenegro" },
    { code: "NL", name: "Netherlands" },
    { code: "MK", name: "North Macedonia" },
    { code: "NO", name: "Norway" },
    { code: "PL", name: "Poland" },
    { code: "PT", name: "Portugal" },
    { code: "RO", name: "Romania" },
    { code: "RU", name: "Russia" },
    { code: "SM", name: "San Marino" },
    { code: "RS", name: "Serbia" },
    { code: "SK", name: "Slovakia" },
    { code: "SI", name: "Slovenia" },
    { code: "ES", name: "Spain" },
    { code: "SE", name: "Sweden" },
    { code: "CH", name: "Switzerland" },
    { code: "TR", name: "Turkey" },
    { code: "UA", name: "Ukraine" },
    { code: "GB", name: "United Kingdom" },
    { code: "VA", name: "Vatican City" },
  ],

  "North America": [
    { code: "AG", name: "Antigua and Barbuda" },
    { code: "BS", name: "Bahamas" },
    { code: "BB", name: "Barbados" },
    { code: "BZ", name: "Belize" },
    { code: "CA", name: "Canada" },
    { code: "CR", name: "Costa Rica" },
    { code: "CU", name: "Cuba" },
    { code: "DM", name: "Dominica" },
    { code: "DO", name: "Dominican Republic" },
    { code: "SV", name: "El Salvador" },
    { code: "GD", name: "Grenada" },
    { code: "GT", name: "Guatemala" },
    { code: "HT", name: "Haiti" },
    { code: "HN", name: "Honduras" },
    { code: "JM", name: "Jamaica" },
    { code: "MX", name: "Mexico" },
    { code: "NI", name: "Nicaragua" },
    { code: "PA", name: "Panama" },
    { code: "KN", name: "Saint Kitts and Nevis" },
    { code: "LC", name: "Saint Lucia" },
    { code: "VC", name: "Saint Vincent and the Grenadines" },
    { code: "TT", name: "Trinidad and Tobago" },
    { code: "US", name: "United States" },
  ],

  "South America": [
    { code: "AR", name: "Argentina" },
    { code: "BO", name: "Bolivia" },
    { code: "BR", name: "Brazil" },
    { code: "CL", name: "Chile" },
    { code: "CO", name: "Colombia" },
    { code: "EC", name: "Ecuador" },
    { code: "GY", name: "Guyana" },
    { code: "PY", name: "Paraguay" },
    { code: "PE", name: "Peru" },
    { code: "SR", name: "Suriname" },
    { code: "UY", name: "Uruguay" },
    { code: "VE", name: "Venezuela" },
  ],

  Asia: [
    { code: "AF", name: "Afghanistan" },
    { code: "AM", name: "Armenia" },
    { code: "AZ", name: "Azerbaijan" },
    { code: "BH", name: "Bahrain" },
    { code: "BD", name: "Bangladesh" },
    { code: "BT", name: "Bhutan" },
    { code: "BN", name: "Brunei" },
    { code: "KH", name: "Cambodia" },
    { code: "CN", name: "China" },
    { code: "GE", name: "Georgia" },
    { code: "IN", name: "India" },
    { code: "ID", name: "Indonesia" },
    { code: "IR", name: "Iran" },
    { code: "IQ", name: "Iraq" },
    { code: "IL", name: "Israel" },
    { code: "JP", name: "Japan" },
    { code: "JO", name: "Jordan" },
    { code: "KZ", name: "Kazakhstan" },
    { code: "KW", name: "Kuwait" },
    { code: "KG", name: "Kyrgyzstan" },
    { code: "LA", name: "Laos" },
    { code: "LB", name: "Lebanon" },
    { code: "MY", name: "Malaysia" },
    { code: "MV", name: "Maldives" },
    { code: "MN", name: "Mongolia" },
    { code: "MM", name: "Myanmar" },
    { code: "NP", name: "Nepal" },
    { code: "KP", name: "North Korea" },
    { code: "OM", name: "Oman" },
    { code: "PK", name: "Pakistan" },
    { code: "PS", name: "Palestine" },
    { code: "PH", name: "Philippines" },
    { code: "QA", name: "Qatar" },
    { code: "SA", name: "Saudi Arabia" },
    { code: "SG", name: "Singapore" },
    { code: "KR", name: "South Korea" },
    { code: "LK", name: "Sri Lanka" },
    { code: "SY", name: "Syria" },
    { code: "TW", name: "Taiwan" },
    { code: "TJ", name: "Tajikistan" },
    { code: "TH", name: "Thailand" },
    { code: "TL", name: "Timor-Leste" },
    { code: "TM", name: "Turkmenistan" },
    { code: "AE", name: "United Arab Emirates" },
    { code: "UZ", name: "Uzbekistan" },
    { code: "VN", name: "Vietnam" },
    { code: "YE", name: "Yemen" },
  ],

  Africa: [
    { code: "DZ", name: "Algeria" },
    { code: "AO", name: "Angola" },
    { code: "BJ", name: "Benin" },
    { code: "BW", name: "Botswana" },
    { code: "BF", name: "Burkina Faso" },
    { code: "BI", name: "Burundi" },
    { code: "CV", name: "Cape Verde" },
    { code: "CM", name: "Cameroon" },
    { code: "CF", name: "Central African Republic" },
    { code: "TD", name: "Chad" },
    { code: "KM", name: "Comoros" },
    { code: "CD", name: "Congo (DRC)" },
    { code: "CG", name: "Congo (Republic)" },
    { code: "CI", name: "Côte d’Ivoire" },
    { code: "DJ", name: "Djibouti" },
    { code: "EG", name: "Egypt" },
    { code: "GQ", name: "Equatorial Guinea" },
    { code: "ER", name: "Eritrea" },
    { code: "SZ", name: "Eswatini" },
    { code: "ET", name: "Ethiopia" },
    { code: "GA", name: "Gabon" },
    { code: "GM", name: "Gambia" },
    { code: "GH", name: "Ghana" },
    { code: "GN", name: "Guinea" },
    { code: "GW", name: "Guinea-Bissau" },
    { code: "KE", name: "Kenya" },
    { code: "LS", name: "Lesotho" },
    { code: "LR", name: "Liberia" },
    { code: "LY", name: "Libya" },
    { code: "MG", name: "Madagascar" },
    { code: "MW", name: "Malawi" },
    { code: "ML", name: "Mali" },
    { code: "MR", name: "Mauritania" },
    { code: "MU", name: "Mauritius" },
    { code: "MA", name: "Morocco" },
    { code: "MZ", name: "Mozambique" },
    { code: "NA", name: "Namibia" },
    { code: "NE", name: "Niger" },
    { code: "NG", name: "Nigeria" },
    { code: "RW", name: "Rwanda" },
    { code: "ST", name: "São Tomé and Príncipe" },
    { code: "SN", name: "Senegal" },
    { code: "SC", name: "Seychelles" },
    { code: "SL", name: "Sierra Leone" },
    { code: "SO", name: "Somalia" },
    { code: "ZA", name: "South Africa" },
    { code: "SS", name: "South Sudan" },
    { code: "SD", name: "Sudan" },
    { code: "TZ", name: "Tanzania" },
    { code: "TG", name: "Togo" },
    { code: "TN", name: "Tunisia" },
    { code: "UG", name: "Uganda" },
    { code: "ZM", name: "Zambia" },
    { code: "ZW", name: "Zimbabwe" },
  ],

  Oceania: [
    { code: "AU", name: "Australia" },
    { code: "FJ", name: "Fiji" },
    { code: "KI", name: "Kiribati" },
    { code: "MH", name: "Marshall Islands" },
    { code: "FM", name: "Micronesia" },
    { code: "NR", name: "Nauru" },
    { code: "NZ", name: "New Zealand" },
    { code: "PW", name: "Palau" },
    { code: "PG", name: "Papua New Guinea" },
    { code: "WS", name: "Samoa" },
    { code: "SB", name: "Solomon Islands" },
    { code: "TO", name: "Tonga" },
    { code: "TV", name: "Tuvalu" },
    { code: "VU", name: "Vanuatu" },
  ],
};


